import { Android, Apple, Close, DesktopMac, Download, Microsoft, QrCode } from '@mui/icons-material';
import { Avatar, Box, Button, Card, CardContent, Chip, Dialog, IconButton, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useEffect, useState } from 'react';
import logoIcon from '../assets/images/icon_logo.png';
import {
  DOWNLOAD_CONFIG_URL,
  DownloadCatalog,
  fileUrl,
  formatFileSize,
  IOS_TESTFLIGHT_URL,
  latestProduction,
  releaseNotes,
  ReleaseFile,
} from '../utils/clientDownloads';

type PlatformCard = {
  name: string;
  icon: React.ReactNode;
  version: string;
  size: string;
  notes: string;
  href: string;
  qrUrl: string;
  primaryLabel: string;
  qrLabel: string;
  mobileOpensDirectly: boolean;
};

const isMobileBrowser = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const ClientDownloads: React.FC = () => {
  const [ios, setIos] = useState<ReleaseFile>();
  const [apk, setApk] = useState<ReleaseFile>();
  const [mac, setMac] = useState<ReleaseFile>();
  const [windows, setWindows] = useState<ReleaseFile>();
  const [failed, setFailed] = useState(false);
  const [qr, setQr] = useState<PlatformCard | null>(null);
  const onPhone = typeof navigator !== 'undefined' && isMobileBrowser();

  useEffect(() => {
    let cancelled = false;
    fetch(DOWNLOAD_CONFIG_URL)
      .then((response) => {
        if (!response.ok) throw new Error('download config unavailable');
        return response.json() as Promise<DownloadCatalog>;
      })
      .then((catalog) => {
        if (cancelled) return;
        setIos(latestProduction(catalog.ipaList, () => true));
        setApk(latestProduction(catalog.apkList, (item) => (item.name || '').endsWith('.apk')));
        setMac(latestProduction(catalog.appList, (item) => (item.name || '').endsWith('.dmg')));
        setWindows(latestProduction(catalog.appList, (item) => (item.name || '').endsWith('.exe')));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cards: PlatformCard[] = [
    {
      name: 'iOS版本',
      icon: <Apple />,
      version: ios?.version ? `v${ios.version}` : 'TestFlight',
      size: formatFileSize(ios?.size),
      notes: releaseNotes(ios?.desc),
      href: IOS_TESTFLIGHT_URL,
      qrUrl: IOS_TESTFLIGHT_URL,
      primaryLabel: 'TestFlight下载',
      qrLabel: '扫码下载',
      mobileOpensDirectly: true,
    },
    {
      name: 'Android版本',
      icon: <Android />,
      version: apk?.version ? `v${apk.version}` : '-',
      size: formatFileSize(apk?.size),
      notes: releaseNotes(apk?.desc),
      href: fileUrl(apk),
      qrUrl: fileUrl(apk),
      primaryLabel: '立即下载',
      qrLabel: '扫码下载',
      mobileOpensDirectly: true,
    },
    {
      name: 'Mac版',
      icon: <DesktopMac />,
      version: mac?.version ? `v${mac.version}` : '-',
      size: formatFileSize(mac?.size),
      notes: releaseNotes(mac?.desc),
      href: fileUrl(mac),
      qrUrl: '',
      primaryLabel: '立即下载',
      qrLabel: '',
      mobileOpensDirectly: false,
    },
    {
      name: 'Windows版',
      icon: <Microsoft />,
      version: windows?.version ? `v${windows.version}` : '-',
      size: formatFileSize(windows?.size),
      notes: releaseNotes(windows?.desc),
      href: fileUrl(windows),
      qrUrl: '',
      primaryLabel: '立即下载',
      qrLabel: '',
      mobileOpensDirectly: false,
    },
  ];

  const openQr = (card: PlatformCard) => {
    if (!card.qrUrl || onPhone) return;
    setQr(card);
  };

  return (
    <Box sx={{ mb: { xs: 5, md: 7 } }}>
      <Typography
        component="h2"
        sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, letterSpacing: '-0.04em' }}
      >
        下载飞舟客户端
      </Typography>
      <Typography sx={{ mt: 1.2, color: 'text.secondary', lineHeight: 1.8 }}>
        移动端与桌面端全覆盖，点击按钮或扫码即可安装
      </Typography>
      {failed && (
        <Typography sx={{ mt: 1.5, color: 'error.main' }}>安装包信息暂时获取不到，请稍后再试。</Typography>
      )}
      <Box
        sx={{
          mt: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {cards.map((card) => (
          <Card
            key={card.name}
            elevation={0}
            sx={{
              position: 'relative',
              height: '100%',
              borderRadius: '18px',
              border: '1px solid rgba(var(--ink-rgb), 0.08)',
              backgroundColor: 'rgb(var(--surface-rgb))',
            }}
          >
            {card.qrUrl && !onPhone && (
              <IconButton
                aria-label={`${card.name}扫码下载`}
                onClick={() => openQr(card)}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  width: 44,
                  height: 44,
                  borderRadius: '14px',
                  color: 'rgb(var(--brand-rgb))',
                  backgroundColor: 'rgba(var(--brand-rgb), 0.08)',
                  border: '1px solid rgba(var(--brand-rgb), 0.16)',
                  '&:hover': { backgroundColor: 'rgba(var(--brand-rgb), 0.16)' },
                  '&:focus-visible': { outline: '2px solid rgb(var(--brand-rgb))', outlineOffset: 2 },
                }}
              >
                <QrCode sx={{ fontSize: 22 }} />
              </IconButton>
            )}
            <CardContent sx={{ p: 3, pt: 3.5, textAlign: 'center', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(var(--ink-rgb), 0.06)',
                  color: 'rgb(var(--ink-rgb))',
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                {card.icon}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{card.name}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5, minHeight: 32 }}>
                <Chip label={card.version} size="small" sx={{ bgcolor: 'rgba(var(--brand-rgb), 0.08)', color: 'rgb(var(--brand-rgb))' }} />
                {card.size && <Chip label={card.size} size="small" sx={{ bgcolor: 'rgba(var(--ink-rgb), 0.05)' }} />}
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  height: '3em',
                  whiteSpace: 'pre-line',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textAlign: 'left',
                  lineHeight: 1.5,
                }}
              >
                {card.notes || ' '}
              </Typography>
              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Download />}
                  disabled={!card.href}
                  onClick={() => {
                    if (card.href) window.location.href = card.href;
                  }}
                  sx={{
                    height: 48,
                    borderRadius: '999px',
                    boxShadow: 'none',
                    backgroundColor: 'rgb(var(--brand-rgb))',
                    '&:hover': { backgroundColor: 'rgb(var(--brand-rgb))', opacity: 0.92, boxShadow: 'none' },
                    '&:focus-visible': { outline: '2px solid rgb(var(--ink-rgb))', outlineOffset: 2 },
                  }}
                >
                  {card.primaryLabel}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Dialog
        open={qr !== null}
        onClose={() => setQr(null)}
        aria-labelledby="download-qr-title"
        disableScrollLock
        slotProps={{
          paper: {
            sx: { borderRadius: '24px', px: { xs: 2.5, sm: 4 }, pt: 2, pb: 3.5, overflow: 'visible' },
          },
        }}
      >
        <Box sx={{ position: 'relative', textAlign: 'center', minWidth: { xs: 260, sm: 320 } }}>
          <IconButton
            aria-label="关闭"
            onClick={() => setQr(null)}
            sx={{
              position: 'absolute',
              top: 0,
              right: -8,
              width: 44,
              height: 44,
              '&:focus-visible': { outline: '2px solid rgb(var(--brand-rgb))', outlineOffset: 2 },
            }}
          >
            <Close />
          </IconButton>
          <Typography id="download-qr-title" variant="h6" sx={{ mt: 1, mb: 2, fontWeight: 700 }}>
            {qr?.name}
          </Typography>
          <Box
            sx={{
              position: 'relative',
              width: 228,
              height: 228,
              mx: 'auto',
              p: 1,
              borderRadius: '20px',
              bgcolor: '#fff',
              border: '1px solid rgba(var(--ink-rgb), 0.08)',
            }}
          >
            {qr && <QRCodeCanvas value={qr.qrUrl} size={212} level="H" includeMargin />}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src={logoIcon} alt="" style={{ width: 28, height: 28, borderRadius: 7 }} />
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mt: 2.2, color: 'text.secondary', lineHeight: 1.7 }}>
            {qr?.name === 'iOS版本' ? (
              <>
                用
                <Box component="span" sx={{ color: 'rgb(var(--brand-rgb))', fontWeight: 700 }}> iPhone 相机 </Box>
                扫码安装
              </>
            ) : (
              '用手机浏览器扫码下载'
            )}
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ClientDownloads;
